import { Component, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Alert } from '../models/alert.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-token-price-alert-viewer',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './token-price-alert-viewer.component.html',
  styleUrls: ['./token-price-alert-viewer.component.scss']
})
export class TokenPriceAlertViewerComponent {
  constructor(private translate: TranslateService) {}
  @Input() alert!: Alert;
  @Input() currencySymbol!: string;
  @Input() isLoading = false;
  @Input() isArabicLang: boolean = false;
  @Input() chainId: number = 1;
  @Input() priceMap: Record<string, number> = {};
  @Input() defaultFiat: string = 'USD';
  @Input() alerts: Alert[] = []; // New input to hold the list of alerts
  @Input() currentIndex: number = 0; // New input to track the current alert index
  @Output() reactivateAlert = new EventEmitter<Alert>();
  @Output() goToSwap = new EventEmitter<Alert>();
  @Output() removeAlert = new EventEmitter<Alert>();
  @Output() indexChanged = new EventEmitter<number>(); // New output to notify parent of index change

  CHAIN_MAP: Record<number, { icon: string; name: string }> = {
    1: { icon: 'assets/chains/ethereum-eth-logo.svg', name: 'Ethereum' },
    42161: { icon: 'assets/chains/arbitrum-arb-logo.svg', name: 'Arbitrum' },
    137: { icon: 'assets/chains/polygon-matic-logo.svg', name: 'Polygon' },
  };
  readonly symbolByChainId: Record<number, string> = {
    1: 'ETH',
    42161: 'ETH',
    137: 'MATIC',
    56: 'BNB',
    10: 'ETH',
    43114: 'AVAX'
  };
  isCollapsed = true;
  private startY: number = 0;
  private currentY: number = 0;
  private threshold = 50; // Minimum distance to detect swipe

  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
  }

  getTokenLabel(symbol?: string): string {
    if (!symbol) return '';
    const key = 'TOKENS.' + symbol.toUpperCase();
    const translated = this.translate.instant(key);
    return translated !== key ? translated : symbol;
  }

  getGainProgress(): number {
    const initial = this.alert?.initialAmountOut;
    const current = this.alert?.liveQuote;

    if (!initial || !current || initial === 0) return 0;

    return ((current - initial) / initial) * 100;
  }

  getGainProgressAbs(): number {
    return Math.abs(this.getGainProgress());
  }

  getFormattedGainProgress(): string {
    const gain = this.getGainProgress();
    const abs = Math.abs(gain);

    const prefix = abs < 0.05 ? '' : (gain > 0 ? '+' : '–');
    return `${prefix}${abs.toFixed(1)}%`;
  }

  getReceivedProgress(): number {
    const initial = this.alert?.initialAmountOut;
    const target = this.alert?.threshold;
    const current = this.alert?.liveQuote;

    if (!initial || !target || !current) return 0;

    const totalGap = target - initial;
    const currentGap = current - initial;

    if (totalGap === 0) return 100;

    return Math.max(-100, Math.min(100, (currentGap / totalGap) * 100));
  }

  getReceivedProgressAbs(): number {
    const raw = this.getReceivedProgress();
    return isFinite(raw) ? Math.min(Math.abs(raw) / 2, 100) : 0;
  }

  getArcDashArray(): string {
    const arcLength = 283; // approx path length for semi-circle
    const progress = this.getReceivedProgress(); // -100 to 100
    const normalized = Math.max(-100, Math.min(100, progress));
    const filled = ((normalized + 100) / 200) * arcLength;
    const gap = arcLength - filled;
    return `${filled} ${gap}`;
  }

  get displayedCurrentGasFiat(): number {
    const nativeAmount = this.alert?.currentGasFee ? this.alert.currentGasFee / 1e9 : 0;
    return nativeAmount;
  }

  get displayedGasFiat(): number {
    return this.alert?.maxGasFee ? this.alert.maxGasFee / 1e9 : 0;
  }

  getExpectedGainPercentage(): string | null {
    const initial = this.alert?.initialAmountOut;
    const target = this.alert?.threshold;

    if (!initial || !target || initial === 0) return null;

    const percent = ((target - initial) / initial) * 100;
    const abs = Math.abs(percent).toFixed(1);
    const prefix = percent > 0 ? '+' : percent < 0 ? '–' : '';

    return `${prefix}${abs}%`;
  }

  getExpectedGainPercentageGas(): string | null {
    const initial = this.alert?.initialGasFee;
    const target = this.alert?.maxGasFee;

    if (!initial || !target || initial === 0) return null;

    const percent = ((target - initial) / initial) * 100;
    const abs = Math.abs(percent).toFixed(1);
    const prefix = percent > 0 ? '+' : percent < 0 ? '–' : '';

    return `${prefix}${abs}%`;
  }

  getProgressTransform(): string {
    const value = this.getReceivedProgressAbs() + 1;
    return this.getReceivedProgress() >= 0
      ? `translateX(${value}%)`
      : `translateX(-${value}%)`;
  }

  isDarkMode(): boolean {
    return document.body.classList.contains('dark-mode');
  }

  readonly segmentCount = 30;
  get segmentArray(): number[] {
    return Array.from({ length: this.segmentCount }, (_, i) => i);
  }

  get coloredSegmentCount(): number {
    const percent = this.getReceivedProgress();
    return Math.round(((percent + 100) / 200) * this.segmentCount);
  }

  getSegmentAngle(i: number): number {
    return 180 - (i / (this.segmentCount - 1)) * 180; // de 180° à 0°
  }

  getSegmentX(i: number, inner: boolean): number {
    const angle = this.getSegmentAngle(i) * (Math.PI / 180);
    const r = inner ? 60 : 75;
    return 100 + r * Math.cos(angle);
  }

  getSegmentY(i: number, inner: boolean): number {
    const angle = this.getSegmentAngle(i) * (Math.PI / 180);
    const r = inner ? 60 : 75;
    return 100 - r * Math.sin(angle);
  }

  getSegmentColor(i: number): string {
    const t = i / (this.segmentCount - 1); // de 0 à 1
    const h = Math.round(t * 120); // arc-en-ciel HSL de rouge → violet
    return `hsl(${h}, 100%, 55%)`;
  }

  readonly gasSegmentCount = 30;
  get gasSegmentArray(): number[] {
    return Array.from({ length: this.gasSegmentCount }, (_, i) => i);
  }

  get getGasProgress(): number {
    const initial = this.alert?.initialGasFee;
    const current = this.alert?.currentGasFee;
    const target = this.alert?.maxGasFee;

    if (!initial || !target || !current) return 0;

    const totalGap = target - initial;
    const currentGap = current - initial;

    if (totalGap === 0) return 100;

    const rawProgress = (currentGap / totalGap) * 100;
    const clamped = Math.max(-100, Math.min(100, rawProgress));

    return clamped;
  }

  get gasColoredSegmentCount(): number {
    const percent = this.getGasProgress;
    return Math.round(((percent + 100) / 200) * this.gasSegmentCount);
  }

  getGasSegmentAngle(i: number): number {
    return 180 - (i / (this.gasSegmentCount - 1)) * 180;
  }

  getGasSegmentX(i: number, inner: boolean): number {
    const angle = this.getGasSegmentAngle(i) * (Math.PI / 180);
    const r = inner ? 60 : 75;
    return 100 + r * Math.cos(angle);
  }

  getGasSegmentY(i: number, inner: boolean): number {
    const angle = this.getGasSegmentAngle(i) * (Math.PI / 180);
    const r = inner ? 60 : 75;
    return 100 - r * Math.sin(angle);
  }

  getGasSegmentColor(i: number): string {
    const t = i / (this.gasSegmentCount - 1);
    const h = Math.round(t * 120); // rouge (0°) → vert (120°)
    return `hsl(${h}, 100%, 50%)`;
  }

  getFormattedGainProgressGas(): string {
    const initial = this.alert?.initialGasFee;
    const current = this.alert?.currentGasFee;

    if (!initial || !current || initial === 0) return "0";

    const val = ((current - initial) / initial) * 100;
    const abs = Math.abs(val);
    const prefix = abs < 0.05 ? '' : (val > 0 ? '+' : '–');
    return `${prefix}${abs.toFixed(1)}%`;
  }

//  @HostListener('touchstart', ['$event']) onTouchStart(e: TouchEvent) {
//  this.startY = e.touches[0].clientY;
//}

//  @HostListener('touchmove', ['$event'])
//  onTouchMove(event: TouchEvent) {
//    this.currentY = event.touches[0].clientY;
//  }

//  @HostListener('touchend', ['$event']) onTouchEnd(e: TouchEvent) {
//  this.currentY = e.changedTouches[0].clientY;
//  const deltaY = this.currentY - this.startY;

//  if (Math.abs(deltaY) > this.threshold) {
//    const direction = deltaY > 0 ? -1 : 1; // swipe down = previous, swipe up = next
//    this.indexChanged.emit(direction);
//  }
//}



}
